import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

// Only the server renders messages. Keep the scroller, bodies, clocks and forms
// mounted; streams append entries or update delivery details, never the frame.
export class SmsFeedController extends Controller {
    static targets = ["scroller", "notice", "newMessages", "readForm"]
    static values = { url: String, latest: { type: Boolean, default: true } }

    connect() {
        this.generation = (this.generation || 0) + 1
        this.stopped = false
        this.busy = false
        this.failures = 0
        this.unseen = 0
        this.deliveryOffset = 0
        this.visibilityChanged = () => {
            this.clearTimer()
            if (!document.hidden) { this.acknowledge(); this.schedule(0) }
        }
        this.beforeCache = () => this.disconnect()
        document.addEventListener("visibilitychange", this.visibilityChanged)
        document.addEventListener("turbo:before-cache", this.beforeCache)
        if (this.latestValue) this.scrollerTarget.scrollTop = this.scrollerTarget.scrollHeight
        this.acknowledge()
        this.schedule()
    }

    disconnect() {
        this.stopped = true
        this.clearTimer()
        this.abortController?.abort()
        this.readAbortController?.abort()
        this.cancelRender?.()
        document.removeEventListener("visibilitychange", this.visibilityChanged)
        document.removeEventListener("turbo:before-cache", this.beforeCache)
    }

    clearTimer() { clearTimeout(this.timer) }
    nearBottom() {
        const scroller = this.scrollerTarget
        return scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 48
    }
    entries() { return [...this.scrollerTarget.querySelectorAll("[data-sms-feed-message-id]")] }
    after() { return this.entries().at(-1)?.dataset.smsFeedMessageId || "0" }
    known() {
        return [...this.scrollerTarget.querySelectorAll("[data-sms-delivery-version]")]
            .map(element => `${element.dataset.smsDeliveryMessageId}:${element.dataset.smsDeliveryVersion}`)
    }

    schedule(delay) {
        this.clearTimer()
        if (this.stopped || document.hidden) return
        this.timer = setTimeout(() => this.refresh(), delay ?? Math.min(60000, 10000 * (2 ** this.failures)))
    }

    // Keep the first visible message at the same pixel, including when an error
    // above it gains height. Native scroll anchoring is disabled on this scroller
    // to avoid compensating for the same change twice.
    readingPosition() {
        const scroller = this.scrollerTarget
        const top = scroller.getBoundingClientRect().top
        const entry = this.entries().find(element => element.getBoundingClientRect().bottom > top)
        return { entry, offset: entry ? entry.getBoundingClientRect().top - top : 0, scrollTop: scroller.scrollTop }
    }

    restorePosition(position) {
        const scroller = this.scrollerTarget
        if (position.entry?.isConnected) {
            const offset = position.entry.getBoundingClientRect().top - scroller.getBoundingClientRect().top
            const delta = offset - position.offset
            if (delta) scroller.scrollTop += delta
        } else if (scroller.scrollTop !== position.scrollTop) scroller.scrollTop = position.scrollTop
    }

    async refresh(event) {
        event?.preventDefault()
        if (this.stopped || this.busy || document.hidden) return
        this.busy = true
        const generation = this.generation
        this.clearTimer()
        let more = false
        try {
            const known = this.known()
            let index = this.deliveryOffset < known.length ? this.deliveryOffset : 0
            // Bound each request and each cycle. Large/offline backlogs keep
            // their exact ID cursor and continue on the next polling cycle.
            for (let round = 0; round < 20; round += 1) {
                const after = this.latestValue ? this.after() : null
                const url = new URL(this.urlValue, window.location.href)
                if (after !== null) url.searchParams.set("after", after)
                for (const version of known.slice(index, index + 50)) url.searchParams.append("known", version)
                index += 50
                const controller = new AbortController()
                this.abortController = controller
                const timeout = setTimeout(() => controller.abort(), 15000)
                let response, html
                try {
                    response = await fetch(url.href, {
                        credentials: "same-origin", cache: "no-store", signal: controller.signal,
                        headers: { Accept: "text/vnd.turbo-stream.html" }
                    })
                    if (this.stopped || generation !== this.generation) return
                    if (response.redirected || response.status === 401 || response.status === 403) {
                        this.stopForAuth()
                        return
                    }
                    if (!response.ok || !response.headers.get("Content-Type")?.startsWith("text/vnd.turbo-stream.html")) {
                        throw new Error("Unable to load message updates")
                    }
                    html = await response.text()
                } finally { clearTimeout(timeout) }
                if (this.stopped || generation !== this.generation || document.hidden) return
                // Capture immediately before rendering, not when fetch started.
                let follow = this.latestValue && this.nearBottom()
                let position = this.readingPosition()
                const arrivals = await this.renderStreams(html, generation, () => {
                    follow = this.latestValue && this.nearBottom()
                    position = this.readingPosition()
                })
                if (this.stopped || generation !== this.generation || document.hidden) return
                if (arrivals && follow) this.scrollerTarget.scrollTop = this.scrollerTarget.scrollHeight
                else if (html.trim()) this.restorePosition(position)
                if (arrivals && !follow) this.unseen += arrivals
                this.updateNewMessages()
                more = response.headers.get("X-SMS-More") === "1"
                if (more && (after === null || this.after() === after)) throw new Error("Missing arrival cursor")
                if (!more && index >= known.length) break
            }
            this.deliveryOffset = index >= known.length ? 0 : index
            this.failures = 0
            this.say(this.latestValue ? "Up to date" : "Viewing older messages")
            this.busy = false
            await this.acknowledge()
        } catch {
            if (!this.stopped && generation === this.generation) {
                this.failures = Math.min(this.failures + 1, 3)
                this.say("Unable to refresh. Your draft is still here; retrying shortly.")
            }
        } finally {
            if (generation === this.generation) {
                this.busy = false
                this.schedule(more && !this.failures ? 0 : undefined)
            }
        }
    }

    async renderStreams(html, generation = this.generation, beforeApply = () => {}) {
        if (!html.trim()) return 0
        const page = new DOMParser().parseFromString(html, "text/html")
        const streams = [...page.querySelectorAll("turbo-stream")]
        if (!streams.length) throw new Error("Missing streams")
        let arrivals = 0
        const selected = streams.filter(stream => {
            const action = stream.getAttribute("action")
            const target = stream.getAttribute("target")
            if (action === "append" && target === this.scrollerTarget.id) {
                const entry = stream.querySelector("template").content.firstElementChild
                if (!/^\d+$/.test(entry?.dataset.smsFeedMessageId) || entry.id !== `sms-message-entry-${entry.dataset.smsFeedMessageId}`) {
                    throw new Error("Missing message entry")
                }
                // Retried responses must not reconnect an existing message body.
                if (this.element.querySelector(`#${entry.id}`)) return false
                arrivals += 1
            } else if (!(action === "update" &&
                    (/^sms-message-\d+-(status|errors)$/.test(target) || ["sms-read-watermark", "sms-resolve-watermark"].includes(target))) &&
                    !(action === "remove" && target === "sms-message-empty")) {
                throw new Error("Unexpected stream target")
            }
            if (!this.element.querySelector(`#${target}`) && !(action === "remove" && target === "sms-message-empty")) {
                throw new Error("Missing stream target")
            }
            return true
        })
        if (!selected.length) return 0
        // renderStreamMessage schedules work; await the actual render callbacks,
        // not a guessed number of animation frames, before acknowledging IDs.
        this.batchSequence = (this.batchSequence || 0) + 1
        const batch = `sms-feed-${generation}-${this.batchSequence}`
        for (const stream of selected) stream.dataset.smsFeedBatch = batch
        await new Promise((resolve, reject) => {
            let remaining = selected.length
            let started = false
            const cleanup = () => {
                clearTimeout(timeout)
                document.removeEventListener("turbo:before-stream-render", beforeRender)
                this.cancelRender = null
            }
            const finish = () => { cleanup(); resolve() }
            const fail = error => { cleanup(); reject(error) }
            const beforeRender = event => {
                if (event.target.dataset.smsFeedBatch !== batch) return
                const render = event.detail.render
                event.detail.render = async element => {
                    try {
                        if (!this.stopped && generation === this.generation && !document.hidden) {
                            if (!started) { beforeApply(); started = true }
                            await render(element)
                        }
                        if (--remaining === 0) finish()
                    } catch (error) { fail(error) }
                }
            }
            const timeout = setTimeout(() => fail(new Error("Stream render timed out")), 15000)
            this.cancelRender = finish
            document.addEventListener("turbo:before-stream-render", beforeRender)
            try { Turbo.renderStreamMessage(selected.map(stream => stream.outerHTML).join("")) }
            catch (error) { fail(error) }
        })
        // Refresh local-day separators immediately on appended targets. Existing
        // timestamps and linkify nodes remain connected and keep their state.
        const clock = this.element.querySelector('[data-controller~="relative-time"]')
        if (arrivals && !this.stopped && generation === this.generation && clock) {
            this.application.getControllerForElementAndIdentifier(clock, "relative-time")?.update()
        }
        return arrivals
    }

    scrolled() {
        this.updateNewMessages()
        if (!this.busy) this.acknowledge()
    }
    updateNewMessages() {
        if (this.nearBottom()) this.unseen = 0
        this.newMessagesTarget.hidden = this.unseen === 0
        this.newMessagesTarget.textContent = `${this.unseen} new message${this.unseen === 1 ? "" : "s"} ↓`
    }
    jumpToLatest() {
        this.scrollerTarget.scrollTop = this.scrollerTarget.scrollHeight
        this.updateNewMessages()
        this.acknowledge()
    }

    async acknowledge() {
        if (this.stopped || this.busy || document.hidden || !this.latestValue || !this.nearBottom() || !this.hasReadFormTarget) return
        const data = new FormData(this.readFormTarget)
        const watermark = data.get("watermark")
        if (!watermark || watermark === this.lastRead || this.readBusy || watermark !== this.after()) return
        this.readBusy = true
        const generation = this.generation
        const controller = new AbortController()
        this.readAbortController = controller
        const timeout = setTimeout(() => controller.abort(), 10000)
        try {
            const response = await fetch(this.readFormTarget.action, {
                method: "POST", body: data, credentials: "same-origin", cache: "no-store",
                signal: controller.signal, headers: { "X-SMS-Poll": "1", Accept: "text/plain" }
            })
            if (this.stopped || generation !== this.generation) return
            if (response.redirected || response.status === 401 || response.status === 403) this.stopForAuth()
            else if (response.status === 204) this.lastRead = watermark
        } catch { /* The manual Mark read action stays available. */ }
        finally { clearTimeout(timeout); this.readBusy = false }
    }

    say(message) { this.noticeTarget.textContent = message }
    stopForAuth() {
        this.stopped = true
        this.clearTimer()
        this.say("Your session expired or access changed. Copy your draft before logging in again.")
    }
}
